import { CustomFieldHint } from "@/components/custom-field/CustomFieldHint.tsx";
import useCustomFieldsHook from "@/hooks/use-custom-fields.hook.tsx";
import { useEntityFormConfig } from "@/queries/use-custom-fields.query.ts";
import {
  ApiCFEntityTypes,
  ApiField,
  ApiFieldTypes,
  ApiSectionTypes,
} from "@/types/custom-field.types.ts";
import { apiApplicants } from "@/api/recruit/applicant.api.ts";
import { FileItem } from "@/components/files/FileItem.tsx";
import {
  useModals,
  Modal,
  ModalProps,
  Box,
  Button,
  ButtonIcon,
  FileDropzone,
  FontAwesomeIcon,
  Input,
  Select,
  Skeleton,
  Stack,
  Textarea,
  Title,
} from "@skailer-com/uikit";
import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import { useFileDropzone } from "@/hooks/use-file-dropzone.hook.ts";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import {
  ApplicantSystemData,
  applicantSystemFieldTypes,
  getApplicantSystemValue,
} from "@/pages/recruit/candidates/applicant-system-fields.ts";
import { BulkUploadCVModal } from "@/pages/recruit/candidates/widgets/BulkUploadCVModal.tsx";
import { useApplicantCreate, useApplicantUpdate } from "@/queries/recruit/use-applicants.query.ts";
import { useSources } from "@/queries/use-sources.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ApiFile, ApiPartialDate } from "@/types/common.types.ts";
import {
  ApiApplicant,
  ApiApplicantContact,
  ApiApplicantSavePayload,
  ApiApplicantShort,
  ApplicantCVSupportedFileTypes,
  ContactType,
} from "@/types/recruit/applicant.types.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import { Fragment, useState } from "react";
import { Controller, FieldValues, useFieldArray, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

type ApplicantFormValues = FieldValues & {
  additional_contacts: (Omit<ApiApplicantContact, "type"> & { type: string })[];
  source_id?: number;
  resume?: string;
};

const fileFieldTypes: ApiFieldTypes[] = [ApiFieldTypes.applicant_cv, ApiFieldTypes.applicant_photo];

const toPartialDate = (date: Date | null | undefined): ApiPartialDate => ({
  year: date?.getFullYear() ?? null,
  month: date ? date.getMonth() + 1 : null,
  day: date?.getDate() ?? null,
});

export interface ApplicantEditModalProps extends ModalProps {
  applicant: ApiApplicantShort & Partial<Pick<ApiApplicant, "custom_fields">>;
  onSave?: (updatedApplicant: ApiApplicantShort) => void;
}

export const ApplicantEditModal = ({ applicant, onSave, ...props }: ApplicantEditModalProps) => {
  const { close, openModal } = useModals();
  const { id } = props;
  const isEdit = applicant.id !== 0;
  const { t } = useTranslation();
  const { handleErrors } = useErrorHandle();
  const { buildFromPartialDate } = useFormatter();
  const config = useEntityFormConfig(ApiCFEntityTypes.applicant);
  const {
    getValueForField,
    getZodSchemaForField,
    transformFormDataToValue,
    getSectionTitle,
    getFieldByType,
    getFieldTitle,
  } = useCustomFieldsHook();
  const { data: sources, isLoading: isSourceLoading } = useSources();

  const [parsedApplicant, setParsedApplicant] = useState<ApiApplicantSavePayload | null>(null);
  const [files, setFiles] = useState<ApiFile[]>([]);
  const [isFileUploading, setIsFileUploading] = useState(false);
  const { onDropRejected } = useFileDropzone({
    unsupportedFileFormatMessage: t("errors:unsupported_file_format", {
      formats: Object.keys(ApplicantCVSupportedFileTypes).join(", ").toLowerCase(),
    }),
  });

  const formFields = config.fields.filter(
    (field) =>
      !fileFieldTypes.includes(field.type) &&
      field.type !== ApiFieldTypes.applicant_additional_contacts
  );
  const systemFields = formFields.filter((field) => field.type !== ApiFieldTypes.custom);
  const nameFields = systemFields.filter(
    (field) =>
      field.type === ApiFieldTypes.applicant_first_name ||
      field.type === ApiFieldTypes.applicant_last_name
  );
  const cvField = isEdit
    ? undefined
    : config.fields.find((field) => field.type === ApiFieldTypes.applicant_cv);

  const toSystemValues = (data: ApplicantSystemData): FieldValues => ({
    ...Object.fromEntries(
      applicantSystemFieldTypes.map((type) => [type, getApplicantSystemValue(type, data)])
    ),
    [ApiFieldTypes.applicant_date_birth]: buildFromPartialDate(data.birth_date),
  });

  const getSystemFormValues = (data: ApplicantSystemData) => {
    const values = toSystemValues(data);
    return Object.fromEntries(
      systemFields
        .filter((field) => field.type in values)
        .map((field) => [field.slug, values[field.type]])
    );
  };

  const fromSystemValues = (values: FieldValues): ApplicantSystemData => ({
    first_name: values[ApiFieldTypes.applicant_first_name] ?? "",
    last_name: values[ApiFieldTypes.applicant_last_name] ?? "",
    middle_name: values[ApiFieldTypes.applicant_middle_name] ?? "",
    location: values[ApiFieldTypes.applicant_location] ?? "",
    contacts: {
      phone: values[ApiFieldTypes.applicant_phone] ?? undefined,
      email: values[ApiFieldTypes.applicant_email] ?? undefined,
      telegram_username: values[ApiFieldTypes.applicant_telegram] ?? undefined,
    },
    current_position: {
      position: values[ApiFieldTypes.applicant_position] ?? "",
      company: values[ApiFieldTypes.applicant_company] ?? "",
    },
    salary: values[ApiFieldTypes.applicant_salary] ?? "",
    birth_date: toPartialDate(values[ApiFieldTypes.applicant_date_birth]),
  });

  const fieldsSchema: Record<string, z.ZodType> = Object.assign(
    {},
    ...formFields.map(getZodSchemaForField)
  );
  const schema: z.ZodType<ApplicantFormValues, ApplicantFormValues> = z
    .object({
      additional_contacts: z.array(
        z.object({
          type: z.string().min(1),
          value: z.string().min(1),
          label: z.string().nullish(),
        }),
        { error: t("errors:field_required") }
      ),
      source_id: z
        .number()
        .refine((sourceId) => !!sources?.some((source) => source.id === sourceId), {
          error: t("errors:field_required"),
        })
        .optional(),
      resume: z.string().optional(),
    })
    .and(z.object(fieldsSchema))
    .superRefine((data, context) => {
      const required = (path: string) =>
        context.addIssue({ code: "custom", path: [path], message: t("errors:field_required") });
      if (nameFields.length && nameFields.every((field) => !String(data[field.slug] ?? "").trim()))
        required(nameFields[0].slug);
      if (cvField?.is_mandatory && !parsedApplicant?.cv_ids?.length) required("cv");
    });

  const formHook = useForm<ApplicantFormValues>({
    mode: "onSubmit",
    resolver: zodResolver(schema),
    values: {
      ...getSystemFormValues(applicant),
      ...Object.assign(
        {},
        ...config.customFields.map((field) =>
          getValueForField(field, applicant.custom_fields, !isEdit)
        )
      ),
      additional_contacts: applicant.additional_contacts ?? [],
      source_id: !isEdit
        ? sources?.find((source) => source.source_type === "manual")?.id
        : applicant.source_id,
      resume: applicant.resume ?? undefined,
    },
    resetOptions: { keepDirtyValues: true },
  });

  const {
    control,
    setValue,
    register,
    handleSubmit,
    formState: { errors },
  } = formHook;

  const serverFieldMap = Object.fromEntries(
    config.customFields.map((field) => [`custom_fields.${field.slug}`, field.slug])
  );
  const applicantCreate = useApplicantCreate(control, serverFieldMap);
  const applicantUpdate = useApplicantUpdate(control, serverFieldMap);

  const onSubmit = handleSubmit(async (data) => {
    if (!config.isSuccess) return;
    const payload: ApiApplicantSavePayload = {
      ...parsedApplicant,
      ...fromSystemValues({
        ...toSystemValues(parsedApplicant ?? applicant),
        ...Object.fromEntries(systemFields.map((field) => [field.type, data[field.slug]])),
      }),
      custom_fields: Object.fromEntries(
        config.customFields.map((field) => [field.slug, transformFormDataToValue(field, data)])
      ),
      additional_contacts: data.additional_contacts as ApiApplicantContact[],
      source_id: data.source_id,
      resume: data.resume,
      photo_file_uuid: parsedApplicant?.photo_file_uuid,
      cv_ids: parsedApplicant?.cv_ids,
    };

    try {
      if (!isEdit) {
        const savedApplicant = await applicantCreate.mutateAsync({ new_applicant: payload });
        analyticsService.trackEvent(analyticEvents.applicants.created, {
          [analyticProperties.id]: savedApplicant.id,
          [analyticProperties.source]: "App",
        });
        onSave?.(savedApplicant);
      } else {
        const savedApplicant = await applicantUpdate.mutateAsync({ id: applicant.id, payload });
        analyticsService.trackEvent(analyticEvents.applicants.edited, {
          [analyticProperties.id]: applicant.id,
        });
        onSave?.(savedApplicant);
      }
      close(id);
    } catch {
      return;
    }
  });

  const onDrop = async (acceptedFiles: File[]) => {
    if (!acceptedFiles.length) return;
    if (acceptedFiles.length > 1) {
      close(id);
      openModal(BulkUploadCVModal, { files: acceptedFiles });
      return;
    }

    const file = acceptedFiles[0];
    const formData = new FormData();
    formData.append("file", file, file.name);
    setIsFileUploading(true);
    const { data, error } = await apiApplicants.uploadFile(formData);
    setIsFileUploading(false);

    if (!data) {
      handleErrors(error);
      return;
    }
    setFiles((prev) => [...prev, ...data.files]);
    Object.entries(getSystemFormValues(data.applicant)).forEach(([slug, value]) =>
      setValue(slug, value, { shouldDirty: true })
    );
    setValue("additional_contacts", data.applicant.additional_contacts, { shouldDirty: true });
    setParsedApplicant(data.applicant);
  };

  const { fields, remove, append } = useFieldArray({
    control,
    name: "additional_contacts",
  });

  const [isAddingContact, setIsAddingContact] = useState(false);
  const [newContact, setNewContact] = useState<{
    type: ContactType;
    label?: string;
    value: string;
  }>({
    type: ContactType.phone,
    label: "",
    value: "",
  });

  const getValueLabel = (type: ContactType) => {
    switch (type) {
      case "phone":
      case "email":
        return t(`core:${type}`);
      case "telegram":
      case "link":
      case "other":
        return t(`common:${type}`);
      default:
        return t("common:value");
    }
  };

  const renderField = (field: ApiField) => {
    if (field.type === ApiFieldTypes.applicant_additional_contacts)
      return (
        <>
          <Title header={getFieldTitle(field)} size="xs" paddingTop paddingBottom />
          <CustomFieldHint hint={field.hint}>
            <Stack gap="sm">
              {fields.map((contact, index) => {
                const contactTypeLabel = getValueLabel(contact.type as ContactType);
                const label = contact.label
                  ? `${contactTypeLabel} • ${contact.label}`
                  : contactTypeLabel;

                return (
                  <Box key={contact.id} className="flex gap-2 items-end w-full">
                    <Box className="flex-1">
                      <Input
                        type="text"
                        label={label}
                        {...register(`additional_contacts.${index}.value`)}
                        error={errors.additional_contacts?.[index]?.value?.message}
                        className="w-full"
                        rightIcon={
                          <ButtonIcon
                            icon={<FontAwesomeIcon icon="fa-light fa-trash" />}
                            variant="plain"
                            onClick={() => remove(index)}
                          />
                        }
                      />
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          </CustomFieldHint>

          <Button
            size={"sm"}
            type={"button"}
            variant={"secondary"}
            onClick={() => setIsAddingContact((prev) => !prev)}
            className="mt-4 mb-4"
          >
            {t("common:add_contact")}
          </Button>

          {isAddingContact && (
            <Stack gap="xs" className="mt-2">
              <Box className="flex gap-2">
                <Box className="w-1/2">
                  <Select
                    emptyTitle={t("common:type")}
                    label={t("common:type")}
                    options={[
                      { title: getValueLabel(ContactType.phone), id: ContactType.phone },
                      { title: getValueLabel(ContactType.email), id: ContactType.email },
                      { title: getValueLabel(ContactType.telegram), id: ContactType.telegram },
                      { title: getValueLabel(ContactType.link), id: ContactType.link },
                      { title: getValueLabel(ContactType.other), id: ContactType.other },
                    ]}
                    value={newContact.type}
                    onChange={(val) => {
                      if (typeof val === "string") {
                        setNewContact((prev) => ({ ...prev, type: val as ContactType }));
                      }
                    }}
                  />
                </Box>
                <Box className="w-1/2">
                  <Input
                    type="text"
                    label={t("common:label")}
                    value={newContact.label}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setNewContact((prev) => ({ ...prev, label: e.target.value }))
                    }
                  />
                </Box>
              </Box>

              <Input
                type="text"
                label={getValueLabel(newContact.type)}
                value={newContact.value}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setNewContact((prev) => ({ ...prev, value: e.target.value }))
                }
              />

              <Box className="flex justify-end gap-2 mt-2">
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => {
                    setIsAddingContact(false);
                    setNewContact({ type: ContactType.phone, label: "", value: "" });
                  }}
                >
                  {t("common:cancel")}
                </Button>
                <Button
                  size="sm"
                  onClick={() => {
                    if (!newContact.value.trim()) return;

                    append({
                      type: newContact.type,
                      label: newContact.label?.trim() || undefined,
                      value: newContact.value.trim(),
                    });

                    setIsAddingContact(false);
                    setNewContact({ type: ContactType.phone, label: "", value: "" });
                  }}
                >
                  {t("common:add")}
                </Button>
              </Box>
            </Stack>
          )}
        </>
      );
    if (field.type === ApiFieldTypes.applicant_cv)
      return (
        <>
          <Title
            header={getFieldTitle(field)}
            size="xs"
            required={field.is_mandatory}
            paddingBottom
          />
          <Stack gap={"md"}>
            <CustomFieldHint hint={field.hint}>
              <FileDropzone
                onDrop={onDrop}
                accept={Object.fromEntries(
                  Object.entries(ApplicantCVSupportedFileTypes).map(([key, value]) => [
                    value,
                    [`.${key.toLowerCase()}`],
                  ])
                )}
                multiple
                maxFiles={10}
                error={errors.cv?.message as string | undefined}
                required={field.is_mandatory}
                rejectDescription={t("errors:unsupported_file_format", {
                  formats: Object.keys(ApplicantCVSupportedFileTypes).join(", ").toLowerCase(),
                })}
                onDropRejected={onDropRejected}
                ctaActionText={t("ats:resume_files_cta")}
                description={t("ats:upload_cv_file_drag_and_drop_description")}
                descriptionSize={"sm"}
                activeDescription={t("common:upload_file_drag_and_drop")}
                isLoading={isFileUploading}
              />
            </CustomFieldHint>
            {files.length > 0 && (
              <Stack gap={"sm"}>
                {files.map((file, index) => (
                  <Box className={"flex"} key={`applicant-file-${index}`}>
                    <FileItem file={file} />
                  </Box>
                ))}
              </Stack>
            )}
          </Stack>
        </>
      );
    return getFieldByType({
      field,
      formHook,
      canView: true,
      canEdit: true,
      fieldValue:
        applicant.custom_fields?.find((item) => item.field.id === field.id)?.value ?? undefined,
    });
  };

  const sections = config.sections
    .map((section) => ({
      ...section,
      // Фото в форме не показываем: оно приходит только из распознанного резюме
      fields: section.fields.filter(
        (field) =>
          field.type !== ApiFieldTypes.applicant_photo &&
          (cvField || field.type !== ApiFieldTypes.applicant_cv)
      ),
    }))
    .filter((section) => section.fields.length);

  return (
    <Modal
      {...props}
      variant={"side"}
      title={t("ats:candidate")}
      layout={"stretch"}
      withCloser={true}
      actions={
        <Stack gap={"sm"}>
          <ServerErrorField errors={errors} />

          <Button
            size={"lg"}
            className={"w-full"}
            type={"submit"}
            isLoading={config.isPending || applicantCreate.isPending || applicantUpdate.isPending}
            disabled={!config.isSuccess || isFileUploading}
            onClick={onSubmit}
          >
            {t("common:save")}
          </Button>
        </Stack>
      }
    >
      <form onSubmit={onSubmit}>
        {config.isPending ? (
          <Stack gap="md">
            <Skeleton className="h-5 w-1/3" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </Stack>
        ) : (
          <>
            {sections.map((section, index) => (
              <Fragment key={section.id}>
                {section.section_type !== ApiSectionTypes.applicant_upload_files && (
                  <Title
                    header={getSectionTitle(section)}
                    size="xs"
                    paddingTop={index !== 0}
                    paddingBottom
                  />
                )}
                {section.fields.map((field) => (
                  <Fragment key={field.id}>{renderField(field)}</Fragment>
                ))}
              </Fragment>
            ))}
            <Controller
              render={({ field: { value, onChange } }) => (
                <Select
                  label={t("common:source")}
                  isLoading={isSourceLoading}
                  searchable={true}
                  options={sources}
                  emptyTitle={t("common:select")}
                  value={value}
                  onChange={onChange}
                  required={true}
                  error={errors.source_id?.message}
                />
              )}
              control={control}
              name={"source_id"}
            />
            <Textarea
              label={t("core:notes")}
              placeholder={t("ats:add_notes")}
              {...register("resume", {
                value: undefined,
              })}
            />
          </>
        )}
      </form>
    </Modal>
  );
};
